import { Grid } from '../../../../Common/Grid'
import { Typography, Button } from '@fullscreendigital/carrefour-dex-fe'
import { TitleHeader } from '../../../Common/TitleHeader'
import { useNotification } from '../../../../../../hooks'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { makeCoreGetRequest } from "../../../../../../utils/api"
import { formatErrorMessage } from '../../../../../../utils/formatting'
import { CircularProgress, Paper } from "@mui/material"
import React, { useState } from "react"
import { displayOrDash } from "../../../helpers";

type Truck = {
  id: number | string
  shopName: string
  truckNumber: string
  supportCounter: number
  sealList: string[]
  registeredDate: string
  createdBy: string
  driverName?: string | null
  transportCode?: string | null
}

const QUERY_KEY = 'registeredTrucks'

const ShippedTrucks = () => {
  const { showNotification } = useNotification()
  const queryClient = useQueryClient()
  const [refreshingId, setRefreshingId] = useState<Truck['id'] | null>(null)

  const { data: trucks = [], isLoading } = useQuery<Truck[]>(
    QUERY_KEY,
    async () => {
      return await makeCoreGetRequest('/shop-reception-mobile/truck/hub/registered', null)
    },
    {
      initialData: [],
      onError: (error: any) => {
        showNotification({
          message: `Error while fetching trucks. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  )

  const { mutate: refreshTruck } = useMutation(
    async (truckId: Truck['id']) => {
      return await makeCoreGetRequest(`/shop-reception-mobile/truck/hub/registered/${truckId}`, null)
    },
    {
      onMutate: (truckId: Truck['id']) => {
        setRefreshingId(truckId)
      },
      onSuccess: (updated: Partial<Truck> & { id?: Truck['id'] }, truckId: Truck['id']) => {
        queryClient.setQueryData<Truck[]>(QUERY_KEY, (old = []) =>
          old.map(t => (t.id === truckId ? { ...t, ...updated } as Truck : t))
        )
      },
      onError: (error: any) => {
        showNotification({
          message: `Nu s-a putut reîmprospăta camionul. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      },
      onSettled: () => setRefreshingId(null)
    }
  )

  if (isLoading) {
    return <CircularProgress color="primary" style={{ margin: '40px auto', display: 'block' }} disableShrink />
  }

  const items = [
    <TitleHeader key="title" title="CAMIOANE EXPEDIATE" />,
    ...(trucks as Truck[])?.map((item) => {
      const needsRefresh = !item.driverName || !item.transportCode

      return (
        <Paper elevation={3} style={{ margin: '10px', padding: '20px' }} key={item.id}>
          <Typography variant='medium' component='p'>Magazin(e) destinație: {item.shopName}</Typography>
          <Typography variant='medium' component='p'>Număr înmatriculare: {item.truckNumber}</Typography>
          <Typography variant='medium' component='p'>Suporti în camion: {item.supportCounter}</Typography>

          <Typography variant="medium" component="p">
            Sigiliu (sigilii):{' '}
            {(item.sealList || [])?.map((seal: string, idx: number) => (
              <span key={idx}>{idx > 0 && ' / '}{seal}</span>
            ))}
          </Typography>

          <Typography variant='medium' component='p'>Dată înregistrare: {item.registeredDate}</Typography>
          <Typography variant='medium' component='p'>Operator: {item.createdBy}</Typography>

          <Typography variant='medium' component='p'>
            Șofer: {displayOrDash(item.driverName)}
          </Typography>
          <Typography variant='medium' component='p'>
            Cod transport: {displayOrDash(item.transportCode)}
          </Typography>

          {needsRefresh && (
            <div style={{ marginTop: 10 }}>
              <Button
                variant="contained"
                onClick={() => refreshTruck(item.id)}
                disabled={refreshingId === item.id}
                label={refreshingId === item.id ? 'Se actualizează...' : 'Reîmprospătează'}
              >
              </Button>
            </div>
          )}
        </Paper>
      )
    })
  ]

  if (!trucks?.length) {
    items.push(
      <Typography
        key="empty"
        variant="light"
        component="p"
        style={{ textAlign: 'center', textTransform: 'uppercase' }}
      >
        Nu existǎ camioane expediate.
      </Typography>
    )
  }

  return (
    <Grid gridContainerStyle={{ marginTop: '20px' }} maxWidth="xs" items={items} itemsPerRow={1} />
  )
}

export default ShippedTrucks
